/**
 * Video Utilities for Automatic Dynamic Aspect Ratio & Orientation Detection.
 * 
 * Priority Rules:
 *  1. Numeric dimensions (videoWidth / videoHeight if available)
 *  2. Explicit database orientation metadata ('portrait', 'square', 'landscape')
 *  3. URL Pattern Detection:
 *     - Portrait (9:16): /reel/, /reels/, /share/r/, /shorts/, TikTok, IG Reels
 *     - Square (1:1): /square/, format=square
 *     - Landscape (16:9): /videos/, /share/v/, /watch, YouTube watch, default
 */

export function isPortraitUrl(url) {
  if (!url) return false;
  const str = String(url).trim().toLowerCase();
  return (
    str.includes('/reel/') ||
    str.includes('/reels/') ||
    str.includes('/share/r/') ||
    str.includes('/shorts/') ||
    str.includes('tiktok.com') ||
    str.includes('instagram.com/reel')
  );
}

export function isSquareUrl(url) {
  if (!url) return false;
  const str = String(url).trim().toLowerCase();
  return (
    str.includes('/square/') ||
    str.includes('aspect=1:1') ||
    str.includes('format=square')
  );
}

export function sanitizeFacebookUrl(url) {
  if (!url) return '';
  let str = String(url).trim();

  if (str.includes('facebook.com/plugins/')) {
    try {
      const parsed = new URL(str);
      const href = parsed.searchParams.get('href');
      if (href) str = href;
    } catch (e) { }
  }

  str = str.replace(/facebook\.com\/share\/r\/([^/?]+)/i, 'facebook.com/reel/$1');
  str = str.replace(/facebook\.com\/share\/v\/([^/?]+)/i, 'facebook.com/videos/$1');
  str = str.replace(/facebook\.com\/share\/p\/([^/?]+)/i, 'facebook.com/posts/$1');

  try {
    const urlObj = new URL(str);
    const vParam = urlObj.searchParams.get('v');
    urlObj.search = '';
    if (vParam) {
      urlObj.searchParams.set('v', vParam);
    }
    return urlObj.toString();
  } catch (e) {
    return str.split('?')[0];
  }
}

export function extractOrientationField(item) {
  if (!item || typeof item === 'string') return null;
  const raw = String(
    item.orientation ||
    item.videoOrientation ||
    item.video_orientation ||
    item.aspectRatio ||
    item.aspect_ratio ||
    item.format ||
    ''
  ).toLowerCase().trim();

  if (raw === 'portrait' || raw === '9:16' || raw === '9/16' || raw === 'vertical') return 'portrait';
  if (raw === 'square' || raw === '1:1' || raw === '1/1') return 'square';
  if (raw === 'landscape' || raw === '16:9' || raw === '16/9' || raw === 'horizontal') return 'landscape';
  return null;
}

export function getVideoOrientation(item) {
  if (!item) return 'landscape';

  // 1. Check explicitly stored or overridden orientation field in object
  const storedOrientation = extractOrientationField(item);
  if (storedOrientation) return storedOrientation;

  // 2. Check numeric width and height if available in record
  if (typeof item === 'object' && item !== null) {
    const width = Number(item.videoWidth || item.width || item.video_width);
    const height = Number(item.videoHeight || item.height || item.video_height);

    if (width > 0 && height > 0) {
      const ratio = width / height;
      if (ratio < 0.85) return 'portrait';
      if (ratio >= 0.85 && ratio <= 1.15) return 'square';
      return 'landscape';
    }
  }

  // 3. Automatic detection based on sanitized URL structure
  const urlStr = typeof item === 'string'
    ? item
    : String(item?.videoLink || item?.link || item?.video_url || item?.fb_url || item?.url || '').trim();

  if (urlStr) {
    const sanitized = sanitizeFacebookUrl(urlStr);
    const str = sanitized.toLowerCase();

    // Portrait / Reel / Short patterns -> 9:16
    if (
      str.includes('/reel/') ||
      str.includes('/reels/') ||
      str.includes('/share/r/') ||
      str.includes('/shorts/') ||
      str.includes('tiktok.com') ||
      str.includes('instagram.com/reel')
    ) {
      return 'portrait';
    }

    // Square patterns -> 1:1
    if (str.includes('/square/') || str.includes('aspect=1:1') || str.includes('format=square')) {
      return 'square';
    }

    // Landscape video patterns -> 16:9
    if (str.includes('/share/v/') || str.includes('/videos/') || str.includes('/watch')) {
      return 'landscape';
    }
  }

  // 4. Default fallback
  return 'landscape';
}

export function getVideoAspectRatio(item) {
  if (!item) return '16 / 9';

  if (typeof item === 'object' && item !== null) {
    const width = Number(item.videoWidth || item.width || item.video_width);
    const height = Number(item.videoHeight || item.height || item.video_height);
    if (width > 0 && height > 0) {
      return `${width} / ${height}`;
    }
  }

  const orientation = getVideoOrientation(item);
  switch (orientation) {
    case 'portrait':
      return '9 / 16';
    case 'square':
      return '1 / 1';
    case 'landscape':
    default:
      return '16 / 9';
  }
}

export function extractFacebookVideoId(url) {
  if (!url) return null;
  const str = String(url).trim();
  const match = str.match(/(?:videos\/|watch\?v=|reel\/|reels\/|share\/[vrp]\/|v=)(\d+)/i);
  if (match && match[1]) {
    return match[1];
  }
  const altMatch = str.match(/\/(\d{8,20})\/?/);
  return altMatch ? altMatch[1] : null;
}

export function autoDetectFacebookOrientation(url) {
  return new Promise((resolve) => {
    if (!url || typeof url !== 'string') return resolve(null);
    const cleanUrl = sanitizeFacebookUrl(url);
    const str = cleanUrl.toLowerCase();

    // Explicit Reel / Short patterns are always vertical 9:16 portrait
    if (
      str.includes('/reel/') ||
      str.includes('/reels/') ||
      str.includes('/share/r/') ||
      str.includes('/shorts/') ||
      str.includes('tiktok.com') ||
      str.includes('instagram.com/reel')
    ) {
      return resolve('portrait');
    }

    // Standard Facebook videos / watch links are landscape 16:9
    if (str.includes('/videos/') || str.includes('/watch') || str.includes('/share/v/') || str.includes('facebook.com')) {
      return resolve('landscape');
    }

    resolve(null);
  });
}

/**
 * Automatically inspects thumbnail image natural dimensions (Image.onload)
 */
export function detectOrientationFromThumbnail(imageUrl) {
  return new Promise((resolve) => {
    if (!imageUrl || typeof imageUrl !== 'string' || imageUrl.trim() === '') {
      return resolve(null);
    }

    // Skip graph.facebook.com avatar pictures & video watch links which are square icons or HTML pages
    if (
      imageUrl.includes('graph.facebook.com') ||
      imageUrl.includes('facebook.com/watch') ||
      imageUrl.includes('facebook.com/videos') ||
      imageUrl.includes('youtube.com/watch')
    ) {
      return resolve(null);
    }

    const img = new Image();
    img.src = imageUrl;
    img.onload = () => {
      if (img.naturalWidth > 0 && img.naturalHeight > 0) {
        if (img.naturalHeight > img.naturalWidth) {
          return resolve('portrait');
        }
        return resolve('landscape');
      }
      resolve(null);
    };
    img.onerror = () => resolve(null);
  });
}

/**
 * Automatically fetches or resolves Facebook video dimensions / orientation
 */
export async function fetchFacebookOembedDimensions(videoUrl) {
  if (!videoUrl || typeof videoUrl !== 'string') return null;

  // First try automatic Facebook cover picture dimension detection
  const detected = await autoDetectFacebookOrientation(videoUrl);
  if (detected) return detected;

  const cleanUrl = sanitizeFacebookUrl(videoUrl);

  try {
    const oembedUrl = `https://www.facebook.com/plugins/video/oembed.json/?url=${encodeURIComponent(cleanUrl)}`;
    const res = await fetch(oembedUrl);
    if (res.ok) {
      const data = await res.json();
      const w = Number(data.width);
      const h = Number(data.height);
      if (w > 0 && h > 0) {
        const ratio = w / h;
        if (ratio < 0.85) return 'portrait';
        if (ratio >= 0.85 && ratio <= 1.15) return 'square';
        return 'landscape';
      }
    }
  } catch (e) {
    // Ignore CORS / network restrictions gracefully
  }

  return null;
}

/**
 * Resolves a thumbnail image URL for a video item or video link string.
 * Priority:
 * 1. Explicit custom image/thumbnail on item object (thumbnail, image, thumb, cover, imageUrl)
 * 2. YouTube HQ cover image if YouTube URL
 * 3. Facebook Graph API cover picture if Facebook video URL
 * 4. Empty string fallback
 */
export function getVideoThumbnail(itemOrUrl) {
  if (!itemOrUrl) return '';

  let customThumb = '';
  let url = '';

  if (typeof itemOrUrl === 'object' && itemOrUrl !== null) {
    customThumb =
      itemOrUrl.img_url ||
      itemOrUrl.image_url ||
      itemOrUrl.thumbnail ||
      itemOrUrl.image ||
      itemOrUrl.photo ||
      itemOrUrl.img ||
      itemOrUrl.pic ||
      itemOrUrl.cover ||
      itemOrUrl.poster ||
      itemOrUrl.thumb ||
      itemOrUrl.imageUrl ||
      itemOrUrl.bgImg ||
      itemOrUrl.bg_img ||
      itemOrUrl.picture ||
      itemOrUrl.banner ||
      '';
    url = itemOrUrl.videoLink || itemOrUrl.link || itemOrUrl.video_url || itemOrUrl.fb_url || itemOrUrl.url || '';
  } else if (typeof itemOrUrl === 'string') {
    url = itemOrUrl.trim();
    if (/\.(jpeg|jpg|gif|png|webp|svg)(\?.*)?$/i.test(url)) {
      return url;
    }
  }

  if (customThumb && typeof customThumb === 'string' && customThumb.trim() !== '') {
    if (!customThumb.includes('facebook.com') && !customThumb.includes('fb.watch')) {
      return customThumb.trim();
    }
  }

  if (!url) return '';

  // YouTube check
  const ytMatch = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|v\/|shorts\/)|youtu\.be\/)([^&?#/]+)/i);
  if (ytMatch && ytMatch[1]) {
    return `https://img.youtube.com/vi/${ytMatch[1]}/hqdefault.jpg`;
  }

  return '';
}

